/* eslint-disable no-underscore-dangle, react/jsx-filename-extension */

import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

// Make sure this image is pulled into dist
import './assets_seller/og-website-image-5.png';
import 'Containers/app/developersWidget/startMsw';

import { OptimizelyProvider } from '@optimizely/react-sdk';
import { StatsigClient, StatsigProvider } from '@statsig/react-bindings';

import { ConfigProvider } from 'Context/config';
import { UserProvider } from 'Context/user';
import { VehicleLookupProvider } from 'Context/vehicleLookup';
import { VehicleProvider } from 'Context/vehicles';

import { optimizelyInstanceFactory } from 'Utilities/analytics/optimizely/optimizely';
import { optimizelyAttributes } from 'Utilities/analytics/optimizely/optimizely.helper';
import { createStatSigUser } from 'Utilities/analytics/statsig/statsig';
import { STATSIG_ENV } from 'Utilities/constants';
import { fastlaneFormatIntState } from 'Utilities/fastlane';
import { User } from 'Utilities/user/@types';

import { App } from 'Containers/app/App';
import Status500 from 'Containers/app/error/500';
import ErrorBoundary from 'Containers/app/error/ErrorBoundary';
import { Checkout } from 'Containers/checkout/Checkout';

import { StoreProvider } from '../shared/store';

import { getOptimizelyUserId, InitialContext, InitialState } from './index.helper';

declare global {
  /* eslint-disable vars-on-top, no-var */
  var Sentry: {
    captureException: (error: unknown, sentryOpts?: unknown) => void;
    setContext: (name: string, context: Record<string, any>) => void;
    setUser: (user: Record<string, any>) => void;
    withScope: (callback: (scope: {
      setFingerprint: (fingerprint: string[]) => void;
      setLevel: (level: string) => void;
    }) => void) => void;
  };

  var DD_RUM: {
    addAction: (action: string, context?: Record<string, any>) => void;
    addError: (error: unknown, context?: Record<string, any>) => void;
    onReady: (callback: () => void) => void;
    setUser: ({ id }: { id?: string | number }) => void;
  };

  var __OPTIMIZELY_DATAFILE__: Record<string, any>;

  /* eslint-enable vars-on-top, no-var */

  interface Window {
    Sentry: {
      captureException: Function;
      captureMessage: Function;
      setContext: Function;
      setUser: Function;
    };
    Trustpilot?: {
      loadFromElement: (element: HTMLElement, flag: boolean) => void;
    };
    __CONTAINER__: string;
    __CONTEXT__: InitialContext;
    __ERROR__?: boolean;
    __INITIAL_STATE__: InitialState;
    __SP_SESSION_TIME__: number;
    dataLayer: Record<string, any>[];
    mswStop?: Function;
    snowplow: Function;
  }
}

export const init = () => {
  const { history } = window;

  // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  // Stop <= iOS 15.4 from booting
  if ([
    !('IntersectionObserver' in window),
    !('locks' in navigator),
    !('permissions' in navigator),
  ].some((v) => v)) {
    return;
  }

  const initialState = window.__INITIAL_STATE__;
  const container = window.__CONTAINER__;
  const { vehicles, ...user } = initialState?.user ?? {} as User;
  const $app = document.getElementById('app') || document.body;

  const optimizelyInstance = optimizelyInstanceFactory(initialState.optimizelySdk);
  const optimizelyUserId = getOptimizelyUserId();

  const optimizelyUser = {
    attributes: { ...optimizelyAttributes({ userId: user?.id }) },
    id: optimizelyUserId,
  };

  if (container === 'checkout') {
    const data = window.__CONTEXT__;
    hydrateRoot($app, (<Checkout {...{ ...data }} />));
  } else if (container === 'error-page') {
    hydrateRoot($app, (<Status500 />));
  } else if (container === 'total-error') {
    hydrateRoot($app, (<p>Sorry, the page encountered an error, please try refreshing</p>));
  } else {
    const statSigUser = createStatSigUser(optimizelyUserId, user?.dpSellerId, vehicles?.at(0)?.enquiry?.dpEnquiryId);
    const statSigClient = new StatsigClient(initialState.statSigKey, statSigUser, {
      environment: { tier: STATSIG_ENV },
    });

    // Do not use StoreProvider going forward!! Want to kill with 🔥🔥🔥🔥
    hydrateRoot(
      $app, (
        (
          <StoreProvider initialState={initialState}>
            <ConfigProvider value={fastlaneFormatIntState(initialState)}>
              <UserProvider value={Object.keys(user).length ? user as User : null}>
                <VehicleProvider value={vehicles}>
                  <VehicleLookupProvider value={vehicles && vehicles[0]}>
                    <BrowserRouter>
                      <OptimizelyProvider optimizely={optimizelyInstance} timeout={3000} user={optimizelyUser}>
                        <StatsigProvider client={statSigClient}>
                          <ErrorBoundary>
                            <App hasError={window.__ERROR__} />
                          </ErrorBoundary>
                        </StatsigProvider>
                      </OptimizelyProvider>
                    </BrowserRouter>
                  </VehicleLookupProvider>
                </VehicleProvider>
              </UserProvider>
            </ConfigProvider>
          </StoreProvider>
        )),
    );
  }
};

init();
